import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  Chip,
  Alert,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  IconButton,
  Tooltip,
  Card,
  CardContent,
  Grid,
  Stepper,
  Step,
  StepLabel,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Checkbox,
  TablePagination
} from '@mui/material';
import {
  Check,
  Close,
  Delete,
  Refresh,
  Search,
  FilterList,
  Block,
  CheckCircle,
  PendingActions,
  Visibility,
  Edit,
  Publish,
  CloudUpload,
  Check as CheckIcon,
  Error as ErrorIcon,
  Download,
  SelectAll,
  Deselect
} from '@mui/icons-material';
import { bookkeepersAPI } from '../services/api';
import { parse } from 'papaparse';

const Bookkeepers = () => {
  const navigate = useNavigate();
  const [bookkeepers, setBookkeepers] = useState([]);
  const [stats, setStats] = useState({
    total: 0,
    approved: 0,
    pending: 0,
    verified: 0,
    rejected: 0,
    expired: 0
  });
  const [loading, setLoading] = useState(true);
  const [statsLoading, setStatsLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [filters, setFilters] = useState({
    status: '',
    state: '',
    zipCode: '',
    search: ''
  });

  const [selectedBookkeepers, setSelectedBookkeepers] = useState([]);

  // Pagination state
  const [pagination, setPagination] = useState({
    page: 0,
    rowsPerPage: 10,
    totalCount: 0
  });

  // Import state
  const [importDialog, setImportDialog] = useState({
    open: false,
    step: 0, // 0: upload, 1: preview, 2: results
    file: null,
    previewData: [],
    importResults: {
      success: 0,
      errors: []
    },
    importing: false
  });

  // Add these lines with your other state variables
const [filterOptions, setFilterOptions] = useState({
  statuses: [],
  states: []
});

  // Existing dialogs state
  const [extendDialog, setExtendDialog] = useState({
    open: false,
    bookkeeper: null,
    expirationMonths: 6
  });

  const [deleteDialog, setDeleteDialog] = useState({
    open: false,
    bookkeeper: null
  });

  const [bulkActionsDialog, setBulkActionsDialog] = useState({
    open: false,
    action: ''
  });

  // Helper functions
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    try {
      return new Date(dateString).toLocaleDateString();
    } catch (error) {
      return 'Invalid Date';
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'approved': return 'success';
      case 'pending': return 'warning';
      case 'rejected': return 'error';
      case 'expired': return 'error';
      default: return 'default';
    }
  };

  const getVerificationColor = (isVerified) => {
    return isVerified ? 'success' : 'default';
  };

  // Helper functions for expiration checking
  const isExpiringSoon = (expirationDate) => {
    if (!expirationDate) return false;
    const expireDate = new Date(expirationDate);
    const today = new Date();
    const thirtyDaysFromNow = new Date();
    thirtyDaysFromNow.setDate(today.getDate() + 30);
    return expireDate <= thirtyDaysFromNow && expireDate > new Date();
  };

  const isExpired = (expirationDate) => {
    if (!expirationDate) return false;
    return new Date(expirationDate) < new Date();
  };

  // Fetch statistics from the database
  const fetchBookkeeperStats = async () => {
    try {
      setStatsLoading(true);
      // Create a request without pagination to get all stats
      const params = {
        page: 1,
        limit: 1, // We just need to get the total count from meta data
        getStats: true // Add a flag to indicate we want statistics
      };

      // console.log('Fetching bookkeeper statistics...');
      const response = await bookkeepersAPI.getAll(params);
      // console.log('Stats API response:', response.data);

      // Try to extract statistics from different response structures
      let totalCount = 0;
      let statsData = {
        total: 0,
        approved: 0,
        pending: 0,
        verified: 0,
        rejected: 0,
        expired: 0
      };

      if (response.data.stats) {
        // If backend returns stats directly
        statsData = response.data.stats;
        totalCount = statsData.total || 0;
      } else if (response.data.meta && response.data.meta.stats) {
        // If stats are in meta object
        statsData = response.data.meta.stats;
        totalCount = statsData.total || 0;
      } else if (response.data.data && response.data.data.stats) {
        // If stats are in data object
        statsData = response.data.data.stats;
        totalCount = statsData.total || 0;
      } else {
        // Fallback: calculate from the paginated data
        // We'll need to fetch all bookkeepers to calculate stats
        const allResponse = await bookkeepersAPI.getAll({ page: 1, limit: 1000 });
        let allBookkeepers = [];
        
        if (allResponse.data.data && Array.isArray(allResponse.data.data.bookkeepers)) {
          allBookkeepers = allResponse.data.data.bookkeepers;
          totalCount = allResponse.data.data.totalCount || allResponse.data.data.total || allBookkeepers.length;
        } else if (Array.isArray(allResponse.data)) {
          allBookkeepers = allResponse.data;
          totalCount = allBookkeepers.length;
        } else if (allResponse.data.bookkeepers) {
          allBookkeepers = allResponse.data.bookkeepers;
          totalCount = allResponse.data.totalCount || allResponse.data.total || allBookkeepers.length;
        } else if (allResponse.data.data && Array.isArray(allResponse.data.data)) {
          allBookkeepers = allResponse.data.data;
          totalCount = allResponse.data.totalCount || allResponse.data.total || allBookkeepers.length;
        }

        // Calculate stats from all bookkeepers
        statsData = {
          total: totalCount,
          approved: allBookkeepers.filter(bk => bk.status === 'approved').length,
          pending: allBookkeepers.filter(bk => bk.status === 'pending').length,
          verified: allBookkeepers.filter(bk => bk.isVerified).length,
          rejected: allBookkeepers.filter(bk => bk.status === 'rejected').length,
          expired: allBookkeepers.filter(bk => 
            bk.expirationDate && new Date(bk.expirationDate) < new Date()
          ).length
        };
      }

      setStats(statsData);
      setPagination(prev => ({
        ...prev,
        totalCount: totalCount
      }));
    } catch (err) {
      console.error('Error fetching bookkeeper statistics:', err);
      // Don't set error here to avoid disrupting the main UI
    } finally {
      setStatsLoading(false);
    }
  };

  // Fetch bookkeepers for the table (paginated)
  const fetchBookkeepers = async () => {
    try {
      setLoading(true);
      const params = {
        page: pagination.page + 1, // Backend usually uses 1-based indexing
        limit: pagination.rowsPerPage
      };
      
      // Add filters to params
      if (filters.status) params.status = filters.status;
      if (filters.state) params.state = filters.state;
      if (filters.zipCode) params.zipCode = filters.zipCode;
      if (filters.search) params.search = filters.search;

      // console.log('Fetching bookkeepers with params:', params);
      const response = await bookkeepersAPI.getAll(params);
      // console.log('Bookkeepers API response:', response.data);

      // Handle the API response structure
      let bookkeepersData = [];
      let totalCount = 0;
      
      if (response.data.data && Array.isArray(response.data.data.bookkeepers)) {
        bookkeepersData = response.data.data.bookkeepers;
        totalCount = response.data.data.totalCount || response.data.data.total || bookkeepersData.length;
      } else if (Array.isArray(response.data)) {
        bookkeepersData = response.data;
        totalCount = bookkeepersData.length;
      } else if (response.data.bookkeepers) {
        bookkeepersData = response.data.bookkeepers;
        totalCount = response.data.totalCount || response.data.total || bookkeepersData.length;
      } else if (response.data.data && Array.isArray(response.data.data)) {
        bookkeepersData = response.data.data;
        totalCount = response.data.totalCount || response.data.total || bookkeepersData.length;
      }

      // console.log('Processed bookkeepers data:', bookkeepersData);
      // console.log('Total count:', totalCount);
      
      setBookkeepers(bookkeepersData);
      setPagination(prev => ({
        ...prev,
        totalCount: totalCount
      }));
      setSelectedBookkeepers([]); // Clear selection on refresh
      setError('');
    } catch (err) {
      console.error('Error fetching bookkeepers:', err);
      setError('Failed to fetch bookkeepers. Please try again.');
      setBookkeepers([]);
      setPagination(prev => ({
        ...prev,
        totalCount: 0
      }));
    } finally {
      setLoading(false);
    }
  };

  // Initial data fetch
  useEffect(() => {
    fetchBookkeeperStats();
    fetchBookkeepers();
    fetchFilterOptions();
  }, [pagination.page, pagination.rowsPerPage]);

  // Refresh stats when filters are applied or data changes
  useEffect(() => {
    if (!loading) {
      fetchBookkeeperStats();
    }
  }, [filters, loading]);

  // Handle pagination changes
  const handleChangePage = (event, newPage) => {
  setPagination(prev => ({
    ...prev,
    page: newPage
  }));
  };

  const handleChangeRowsPerPage = (event) => {
  const newRowsPerPage = parseInt(event.target.value, 10);
  setPagination(prev => ({
    page: 0, // Reset to first page
    rowsPerPage: newRowsPerPage,
    totalCount: prev.totalCount
  }));
  };

  // Add this function with your other fetch functions
const fetchFilterOptions = async () => {
  try {
    const response = await bookkeepersAPI.getFilterOptions();
    if (response.data.success) {
      setFilterOptions(response.data.data);
    }
  } catch (err) {
    console.error('Error fetching filter options:', err);
  }
};

  const handleFilterChange = (field, value) => {
    setFilters(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const applyFilters = () => {
    setPagination(prev => ({ ...prev, page: 0 })); // Reset to first page when filtering
    fetchBookkeepers();
  };

  const clearFilters = () => {
    setFilters({
      status: '',
      state: '',
      zipCode: '',
      search: ''
    });
    setPagination(prev => ({ ...prev, page: 0 })); // Reset to first page when clearing filters
    // Re-fetch without filters after a short delay
    setTimeout(() => fetchBookkeepers(), 100);
  };

  const refreshAllData = () => {
    fetchBookkeeperStats();
    fetchBookkeepers();
  };

  // Get unique states and statuses for filters
  // const uniqueStates = [...new Set(bookkeepers.map(bk => bk.state).filter(Boolean))];
  // const uniqueStatuses = [...new Set(bookkeepers.map(bk => bk.status).filter(Boolean))];

  const safeBookkeepers = Array.isArray(bookkeepers) ? bookkeepers : [];

  // Selection handlers
  const handleSelectAll = (event) => {
    if (event.target.checked) {
      setSelectedBookkeepers(safeBookkeepers.map(bk => bk._id));
    } else {
      setSelectedBookkeepers([]);
    }
  };

  const handleSelectBookkeeper = (bookkeeperId) => {
    setSelectedBookkeepers(prev => 
      prev.includes(bookkeeperId)
        ? prev.filter(id => id !== bookkeeperId)
        : [...prev, bookkeeperId]
    );
  };

  const isAllSelected = safeBookkeepers.length > 0 && selectedBookkeepers.length === safeBookkeepers.length;
  const isSomeSelected = selectedBookkeepers.length > 0 && selectedBookkeepers.length < safeBookkeepers.length;

  // View and Edit functions
  const handleView = (bookkeeperId) => {
    navigate(`/bookkeepers/${bookkeeperId}`);
  };

  const handleEdit = (bookkeeperId) => {
    navigate(`/bookkeepers/${bookkeeperId}/edit`);
  };

  // Import functions
  const handleImportOpen = () => {
    setImportDialog({
      open: true,
      step: 0,
      file: null,
      previewData: [],
      importResults: { success: 0, errors: [] },
      importing: false
    });
  };

  const handleFileUpload = (event) => {
    const file = event.target.files[0];
    if (!file) return;

    if (!file.name.endsWith('.csv')) {
      setError('Please upload a CSV file');
      return;
    }

    setImportDialog(prev => ({ ...prev, file }));

    // Parse CSV file
    parse(file, {
      header: true,
      skipEmptyLines: true,
      complete: (results) => {
        // console.log('Parsed CSV data:', results);
        if (results.errors.length > 0) {
          setError(`CSV parsing error: ${results.errors[0].message}`);
          return;
        }
        
        // Validate required fields
        const validatedData = results.data.map((row, index) => {
          const errors = [];
          if (!row.fullName) errors.push('fullName is required');
          if (!row.email) errors.push('email is required');
          
          return {
            ...row,
            _rowId: index + 1,
            errors,
            experience: row.experience ? parseInt(row.experience) : 0,
            hourlyRate: row.hourlyRate ? parseFloat(row.hourlyRate) : 0,
            qualifications: row.qualifications ? row.qualifications.split(',').map(q => q.trim()).filter(q => q) : [],
            specialties: row.specialties ? row.specialties.split(',').map(s => s.trim()).filter(s => s) : []
          };
        });

        setImportDialog(prev => ({
          ...prev,
          previewData: validatedData,
          step: 1
        }));
      },
      error: (error) => {
        setError(`Error parsing CSV: ${error.message}`);
      }
    });
  };

  const handleImport = async () => {
    setImportDialog(prev => ({ ...prev, importing: true }));
    
    const results = {
      success: 0,
      errors: []
    };

    for (const row of importDialog.previewData) {
      try {
        if (row.errors.length > 0) {
          results.errors.push(`Row ${row._rowId}: ${row.errors.join(', ')}`);
          continue;
        }

        // Prepare bookkeeper data
        const bookkeeperData = {
          fullName: row.fullName,
          email: row.email,
          phone: row.phone || '',
          address: row.address || '',
          city: row.city || '',
          state: row.state || '',
          zipCode: row.zipCode || '',
          companyName: row.companyName || '',
          companyAddress: row.companyAddress || '',
          bio: row.bio || '',
          experience: row.experience || 0,
          hourlyRate: row.hourlyRate || 0,
          qualifications: row.qualifications || [],
          specialties: row.specialties || [],
          preferredContact: row.preferredContact || 'email',
          status: row.status || 'pending',
          isActive: true,
          isVerified: false
        };

        // Create bookkeeper via API
        await bookkeepersAPI.create(bookkeeperData);
        results.success++;
      } catch (error) {
        // Enhanced error handling for duplicate emails
        if (error.response?.status === 409) {
          results.errors.push(`Row ${row._rowId}: Email "${row.email}" already exists`);
        } else {
          results.errors.push(`Row ${row._rowId}: ${error.response?.data?.message || error.message}`);
        }
      }
    }

    setImportDialog(prev => ({
      ...prev,
      step: 2,
      importResults: results,
      importing: false
    }));

    // Refresh all data including stats
    refreshAllData();
  };

  const handleImportClose = () => {
    setImportDialog({
      open: false,
      step: 0,
      file: null,
      previewData: [],
      importResults: { success: 0, errors: [] },
      importing: false
    });
  };

  const downloadTemplate = () => {
    const template = `fullName,email,phone,address,city,state,zipCode,companyName,companyAddress,bio,experience,hourlyRate,qualifications,specialties,preferredContact,status
John Doe,john@example.com,555-123-4567,123 Main St,New York,NY,10001,ABC Accounting,123 Business Ave,Experienced bookkeeper with 5+ years,5,75,"CPA,QuickBooks Certified","Small Business,Non-Profit",email,approved
Jane Smith,jane@example.com,555-987-6543,456 Oak Ave,Los Angeles,CA,90210,Jane's Bookkeeping,456 Corporate Blvd,Specialized in tax preparation,8,85,"EA,CMA","Tax Preparation,Payroll",phone,pending`;

    const blob = new Blob([template], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = 'bookkeepers_template.csv';
    link.click();
    window.URL.revokeObjectURL(url);
  };

  // Export functionality
  const handleExport = () => {
    const headers = [
      'Full Name', 'Email', 'Phone', 'Address', 'City', 'State', 'Zip Code',
      'Company Name', 'Company Address', 'Bio', 'Experience (years)', 'Hourly Rate',
      'Qualifications', 'Specialties', 'Preferred Contact', 'Status', 'Verified',
      'Created Date', 'Expiration Date'
    ];

    const csvData = safeBookkeepers.map(bookkeeper => [
      bookkeeper.fullName || '',
      bookkeeper.email || '',
      bookkeeper.phone || '',
      bookkeeper.address || '',
      bookkeeper.city || '',
      bookkeeper.state || '',
      bookkeeper.zipCode || '',
      bookkeeper.companyName || '',
      bookkeeper.companyAddress || '',
      bookkeeper.bio || '',
      bookkeeper.experience || 0,
      bookkeeper.hourlyRate || 0,
      Array.isArray(bookkeeper.qualifications) ? bookkeeper.qualifications.join('; ') : '',
      Array.isArray(bookkeeper.specialties) ? bookkeeper.specialties.join('; ') : '',
      bookkeeper.preferredContact || 'email',
      bookkeeper.status || '',
      bookkeeper.isVerified ? 'Yes' : 'No',
      bookkeeper.createdAt ? new Date(bookkeeper.createdAt).toLocaleDateString() : '',
      bookkeeper.expirationDate ? new Date(bookkeeper.expirationDate).toLocaleDateString() : ''
    ]);

    const csvContent = [
      headers.join(','),
      ...csvData.map(row => row.map(field => `"${field}"`).join(','))
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `bookkeepers_export_${new Date().toISOString().split('T')[0]}.csv`;
    link.click();
    window.URL.revokeObjectURL(url);

    setSuccess('Bookkeepers exported successfully!');
  };

  // Bulk actions
  const handleBulkApprove = async () => {
    try {
      for (const id of selectedBookkeepers) {
        await bookkeepersAPI.approve(id, { expirationMonths: 12 });
      }
      setSuccess(`Successfully approved ${selectedBookkeepers.length} bookkeepers`);
      setSelectedBookkeepers([]);
      refreshAllData();
      setBulkActionsDialog({ open: false, action: '' });
    } catch (err) {
      setError('Failed to approve bookkeepers');
    }
  };

  const handleBulkDelete = async () => {
  try {
    const selectedIds = [...selectedBookkeepers];
    const count = selectedIds.length;
    
    console.log(`🗑️ Attempting bulk delete of ${count} bookkeepers:`, selectedIds);
    
    // Delete all selected bookkeepers
    const deletePromises = selectedIds.map(id => bookkeepersAPI.delete(id));
    const results = await Promise.allSettled(deletePromises);
    
    // Count successes and failures
    const successes = results.filter(r => r.status === 'fulfilled').length;
    const failures = results.filter(r => r.status === 'rejected').length;
    
    console.log(`✅ Bulk delete results: ${successes} succeeded, ${failures} failed`);
    
    if (failures === 0) {
      setSuccess(`Successfully deleted ${count} bookkeeper(s)`);
    } else if (successes > 0) {
      setSuccess(`Deleted ${successes} bookkeeper(s). ${failures} failed.`);
    } else {
      setError('Failed to delete all selected bookkeepers.');
    }
    
    // Remove successfully deleted items from UI state
    if (successes > 0) {
      setBookkeepers(prevBookkeepers => {
        const newBookkeepers = prevBookkeepers.filter(bk => !selectedIds.includes(bk._id));
        console.log(`🔄 Removed from UI. Old count: ${prevBookkeepers.length}, New count: ${newBookkeepers.length}`);
        return newBookkeepers;
      });
      
      // Update the total count
      setPagination(prev => {
        const newTotal = Math.max(0, prev.totalCount - successes);
        console.log(`📊 Updated total count: ${prev.totalCount} -> ${newTotal}`);
        return {
          ...prev,
          totalCount: newTotal
        };
      });
    }
    
    // Update selection
    setSelectedBookkeepers([]);
    setBulkActionsDialog({ open: false, action: '' });
    
    // Refresh stats
    await fetchBookkeeperStats();
    
    // Force a complete refresh after 1 second
    setTimeout(() => {
      console.log('🔄 Forcing full refresh...');
      fetchBookkeepers();
    }, 1000);
    
  } catch (err) {
    console.error('❌ Error in bulk delete:', err);
    setError('Failed to delete bookkeepers. Please try again.');
    
    // Even if failed, refresh to get current state
    fetchBookkeepers();
    fetchBookkeeperStats();
  }
};

  const handleVerifyToggle = async (bookkeeper) => {
    try {
      const newVerifiedStatus = !bookkeeper.isVerified;
      // Since we don't have a direct verify endpoint, we'll use update
      await bookkeepersAPI.update(bookkeeper._id, {
        isVerified: newVerifiedStatus
      });
      
      setSuccess(`Bookkeeper ${bookkeeper.fullName} ${newVerifiedStatus ? 'verified' : 'unverified'} successfully!`);
      refreshAllData();
    } catch (err) {
      setError('Failed to update verification status');
      console.error('Error updating verification:', err);
    }
  };

  const handleExtendExpiration = async () => {
    try {
      await bookkeepersAPI.extend(extendDialog.bookkeeper._id, {
        expirationMonths: extendDialog.expirationMonths
      });
      
      setSuccess(`Bookkeeper ${extendDialog.bookkeeper.fullName} expiration extended successfully!`);
      setExtendDialog({ open: false, bookkeeper: null, expirationMonths: 6 });
      refreshAllData();
    } catch (err) {
      setError('Failed to extend expiration');
      console.error('Error extending expiration:', err);
    }
  };

  const handleDelete = async () => {
  try {
    const bookkeeperId = deleteDialog.bookkeeper._id;
    const bookkeeperName = deleteDialog.bookkeeper.fullName;
    
    console.log(`🗑️ Attempting to delete bookkeeper: ${bookkeeperId} - ${bookkeeperName}`);
    
    // Call the API to delete
    const response = await bookkeepersAPI.delete(bookkeeperId);
    console.log('✅ Delete API response:', response.data);
    
    // Show success message
    setSuccess(`Bookkeeper ${bookkeeperName} deleted successfully!`);
    
    // Close the dialog
    setDeleteDialog({ open: false, bookkeeper: null });
    
    // IMMEDIATELY remove from the UI state
    setBookkeepers(prevBookkeepers => {
      const newBookkeepers = prevBookkeepers.filter(bk => bk._id !== bookkeeperId);
      console.log(`🔄 Removed from UI. Old count: ${prevBookkeepers.length}, New count: ${newBookkeepers.length}`);
      
      // If we're on the last page and it becomes empty, we need to handle this
      if (newBookkeepers.length === 0 && pagination.page > 0) {
        setTimeout(() => {
          setPagination(prev => ({
            ...prev,
            page: prev.page - 1
          }));
        }, 100);
      }
      
      return newBookkeepers;
    });
    
    // Update the total count
    setPagination(prev => {
      const newTotal = Math.max(0, prev.totalCount - 1);
      console.log(`📊 Updated total count: ${prev.totalCount} -> ${newTotal}`);
      return {
        ...prev,
        totalCount: newTotal
      };
    });
    
    // Also refresh stats
    await fetchBookkeeperStats();
    
  } catch (err) {
    console.error('❌ Error deleting bookkeeper:', err);
    
    let errorMessage = 'Failed to delete bookkeeper. Please try again.';
    
    if (err.response?.status === 404) {
      errorMessage = 'Bookkeeper not found. It may have already been deleted.';
    } else if (err.response?.data?.message) {
      errorMessage = `Failed to delete: ${err.response.data.message}`;
    }
    
    setError(errorMessage);
  }
};

  return (
    <Box>
      {/* Header */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4" component="h1">
          Bookkeepers Management
        </Typography>
        <Box sx={{ display: 'flex', gap: 2 }}>
          <Button
            variant="contained"
            startIcon={<CloudUpload />}
            onClick={handleImportOpen}
          >
            Import CSV
          </Button>
          <Button
            variant="outlined"
            startIcon={<Download />}
            onClick={handleExport}
          >
            Export CSV
          </Button>
          <Button
            variant="outlined"
            startIcon={<Refresh />}
            onClick={refreshAllData}
            disabled={loading || statsLoading}
          >
            Refresh All
          </Button>
        </Box>
      </Box>

      {/* Stats Cards */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                Total Bookkeepers
              </Typography>
              {statsLoading ? (
                <CircularProgress size={24} />
              ) : (
                <Typography variant="h4" component="div">
                  {stats.total}
                </Typography>
              )}
              <Typography variant="caption" color="textSecondary">
                All bookkeepers in database
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                Approved
              </Typography>
              {statsLoading ? (
                <CircularProgress size={24} />
              ) : (
                <Typography variant="h4" component="div" color="success.main">
                  {stats.approved}
                </Typography>
              )}
              <Typography variant="caption" color="textSecondary">
                {stats.approved > 0 ? `${((stats.approved / stats.total) * 100).toFixed(1)}% of total` : 'No approved bookkeepers'}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                Pending
              </Typography>
              {statsLoading ? (
                <CircularProgress size={24} />
              ) : (
                <Typography variant="h4" component="div" color="warning.main">
                  {stats.pending}
                </Typography>
              )}
              <Typography variant="caption" color="textSecondary">
                Awaiting approval
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                Verified
              </Typography>
              {statsLoading ? (
                <CircularProgress size={24} />
              ) : (
                <Typography variant="h4" component="div" color="info.main">
                  {stats.verified}
                </Typography>
              )}
              <Typography variant="caption" color="textSecondary">
                {stats.verified > 0 ? `${((stats.verified / stats.total) * 100).toFixed(1)}% of total` : 'No verified bookkeepers'}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Bulk Actions Bar */}
      {selectedBookkeepers.length > 0 && (
        <Paper sx={{ p: 2, mb: 2, bgcolor: 'primary.light', color: 'white' }}>
          <Box display="flex" alignItems="center" justifyContent="space-between">
            <Typography>
              {selectedBookkeepers.length} bookkeeper(s) selected
            </Typography>
            <Box display="flex" gap={1}>
              <Button
                variant="contained"
                color="success"
                size="small"
                onClick={() => setBulkActionsDialog({ open: true, action: 'approve' })}
              >
                Approve Selected
              </Button>
              <Button
                variant="contained"
                color="error"
                size="small"
                onClick={() => setBulkActionsDialog({ open: true, action: 'delete' })}
              >
                Delete Selected
              </Button>
              <Button
                variant="outlined"
                size="small"
                sx={{ color: 'white', borderColor: 'white' }}
                onClick={() => setSelectedBookkeepers([])}
              >
                Clear Selection
              </Button>
            </Box>
          </Box>
        </Paper>
      )}

      {/* Filters */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Box display="flex" alignItems="center" gap={2} flexWrap="wrap">
          <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center' }}>
            <FilterList sx={{ mr: 1 }} />
            Filters
          </Typography>
          
          <FormControl size="small" sx={{ minWidth: 120 }}>
  <InputLabel>Status</InputLabel>
  <Select
    value={filters.status}
    label="Status"
    onChange={(e) => handleFilterChange('status', e.target.value)}
  >
    <MenuItem value="">All</MenuItem>
    {filterOptions.statuses.map(status => (
      <MenuItem key={status} value={status}>
        {status.charAt(0).toUpperCase() + status.slice(1)}
      </MenuItem>
    ))}
  </Select>
</FormControl>

          <FormControl size="small" sx={{ minWidth: 120 }}>
  <InputLabel>State</InputLabel>
  <Select
    value={filters.state}
    label="State"
    onChange={(e) => handleFilterChange('state', e.target.value)}
  >
    <MenuItem value="">All</MenuItem>
    {filterOptions.states.map(state => (
      <MenuItem key={state} value={state}>
        {state}
      </MenuItem>
    ))}
  </Select>
</FormControl>

          <TextField
            size="small"
            label="Zip Code"
            value={filters.zipCode}
            onChange={(e) => handleFilterChange('zipCode', e.target.value)}
            sx={{ minWidth: 120 }}
          />

          <TextField
            size="small"
            label="Search"
            value={filters.search}
            onChange={(e) => handleFilterChange('search', e.target.value)}
            placeholder="Name, email, company..."
            sx={{ minWidth: 200 }}
            InputProps={{
              startAdornment: <Search sx={{ color: 'text.secondary', mr: 1 }} />
            }}
          />

          <Button variant="contained" onClick={applyFilters}>
            Apply
          </Button>
          <Button variant="outlined" onClick={clearFilters}>
            Clear
          </Button>
        </Box>
      </Paper>

      {/* Alerts */}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>
          {success}
        </Alert>
      )}

      {/* Bookkeepers Table */}
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell padding="checkbox">
                <Checkbox
                  indeterminate={isSomeSelected}
                  checked={isAllSelected}
                  onChange={handleSelectAll}
                />
              </TableCell>
              <TableCell><strong>Name</strong></TableCell>
              <TableCell><strong>Contact</strong></TableCell>
              <TableCell><strong>Location</strong></TableCell>
              <TableCell><strong>Company</strong></TableCell>
              <TableCell><strong>Status</strong></TableCell>
              <TableCell><strong>Verified</strong></TableCell>
              <TableCell><strong>Created</strong></TableCell>
              <TableCell><strong>Expires</strong></TableCell>
              <TableCell><strong>Actions</strong></TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={10} align="center">
                  <Box py={3}>
                    <CircularProgress />
                    <Typography variant="body1" sx={{ mt: 1 }}>
                      Loading bookkeepers...
                    </Typography>
                  </Box>
                </TableCell>
              </TableRow>
            ) : safeBookkeepers.length === 0 ? (
              <TableRow>
                <TableCell colSpan={10} align="center">
                  <Box py={3}>
                    <Typography variant="body1" color="textSecondary">
                      No bookkeepers found
                    </Typography>
                    <Button 
                      variant="outlined" 
                      onClick={refreshAllData}
                      sx={{ mt: 2 }}
                    >
                      Refresh
                    </Button>
                  </Box>
                </TableCell>
              </TableRow>
            ) : (
              safeBookkeepers.map((bookkeeper) => (
                <TableRow key={bookkeeper._id} hover>
                  <TableCell padding="checkbox">
                    <Checkbox
                      checked={selectedBookkeepers.includes(bookkeeper._id)}
                      onChange={() => handleSelectBookkeeper(bookkeeper._id)}
                    />
                  </TableCell>
                  <TableCell>
                    <Typography variant="subtitle2" fontWeight="bold">
                      {bookkeeper.fullName}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Box>
                      <div>{bookkeeper.email || 'No email'}</div>
                      <div style={{ fontSize: '0.875rem', color: 'text.secondary' }}>
                        {bookkeeper.phone || 'No phone'}
                      </div>
                    </Box>
                  </TableCell>
                  <TableCell>
                    {bookkeeper.city && `${bookkeeper.city}, ${bookkeeper.state}`}
                    {bookkeeper.zipCode && ` ${bookkeeper.zipCode}`}
                  </TableCell>
                  <TableCell>
                    {bookkeeper.companyName || 'N/A'}
                  </TableCell>
                  <TableCell>
                    <Chip 
                      label={bookkeeper.status || 'unknown'} 
                      size="small" 
                      color={getStatusColor(bookkeeper.status)}
                    />
                  </TableCell>
                  <TableCell>
                    <Chip 
                      label={bookkeeper.isVerified ? 'Verified' : 'Not Verified'} 
                      size="small" 
                      color={getVerificationColor(bookkeeper.isVerified)}
                      variant={bookkeeper.isVerified ? 'filled' : 'outlined'}
                    />
                  </TableCell>
                  <TableCell>
                    {formatDate(bookkeeper.createdAt)}
                  </TableCell>
                  <TableCell>
                    {bookkeeper.expirationDate ? (
                      <Box>
                        <Typography variant="body2">
                          {formatDate(bookkeeper.expirationDate)}
                        </Typography>
                        {isExpiringSoon(bookkeeper.expirationDate) && (
                          <Chip 
                            label="Expiring Soon" 
                            size="small" 
                            color="warning"
                            sx={{ mt: 0.5 }}
                          />
                        )}
                        {isExpired(bookkeeper.expirationDate) && (
                          <Chip 
                            label="Expired" 
                            size="small" 
                            color="error"
                            sx={{ mt: 0.5 }}
                          />
                        )}
                      </Box>
                    ) : (
                      <Typography variant="body2" color="text.secondary">
                        No expiration
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', gap: 0.5 }}>
                      {/* View Button */}
                      <Tooltip title="View Details">
                        <IconButton
                          size="small"
                          color="primary"
                          onClick={() => handleView(bookkeeper._id)}
                        >
                          <Visibility />
                        </IconButton>
                      </Tooltip>

                      {/* Edit Button */}
                      <Tooltip title="Edit Bookkeeper">
                        <IconButton
                          size="small"
                          color="secondary"
                          onClick={() => handleEdit(bookkeeper._id)}
                        >
                          <Edit />
                        </IconButton>
                      </Tooltip>

                      <Tooltip title="Toggle Verification">
                        <IconButton
                          size="small"
                          color={bookkeeper.isVerified ? "default" : "success"}
                          onClick={() => handleVerifyToggle(bookkeeper)}
                        >
                          {bookkeeper.isVerified ? <Block /> : <CheckCircle />}
                        </IconButton>
                      </Tooltip>

                      <Tooltip title="Extend Expiration">
                        <IconButton
                          size="small"
                          color="primary"
                          onClick={() => setExtendDialog({
                            open: true,
                            bookkeeper: bookkeeper,
                            expirationMonths: 6
                          })}
                        >
                          <PendingActions />
                        </IconButton>
                      </Tooltip>

                      <Tooltip title="Delete">
                        <IconButton
                          size="small"
                          color="error"
                          onClick={() => setDeleteDialog({
                            open: true,
                            bookkeeper: bookkeeper
                          })}
                        >
                          <Delete />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Pagination */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mt={2} p={2}>
        <Typography variant="body2" color="text.secondary">
          Showing {safeBookkeepers.length} of {pagination.totalCount} bookkeepers
        </Typography>
        
        <TablePagination
          component="div"
          count={pagination.totalCount}
          page={pagination.page}
          onPageChange={handleChangePage}
          rowsPerPage={pagination.rowsPerPage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          rowsPerPageOptions={[10, 20, 50, 100]}
          labelRowsPerPage="Rows per page:"
        />
      </Box>

      {/* Import Dialog */}
      <Dialog 
        open={importDialog.open} 
        onClose={handleImportClose}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          Import Bookkeepers from CSV
        </DialogTitle>
        
        <DialogContent>
          <Stepper activeStep={importDialog.step} sx={{ mb: 3 }}>
            <Step>
              <StepLabel>Upload CSV</StepLabel>
            </Step>
            <Step>
              <StepLabel>Preview Data</StepLabel>
            </Step>
            <Step>
              <StepLabel>Import Results</StepLabel>
            </Step>
          </Stepper>

          {importDialog.step === 0 && (
            <Box>
              <Typography variant="body1" paragraph>
                Upload a CSV file with bookkeeper data. Download the template below for the correct format.
              </Typography>
              
              <Button
                variant="outlined"
                onClick={downloadTemplate}
                sx={{ mb: 2 }}
              >
                Download Template
              </Button>

              <Box
                sx={{
                  border: '2px dashed',
                  borderColor: 'primary.main',
                  borderRadius: 2,
                  p: 3,
                  textAlign: 'center',
                  cursor: 'pointer'
                }}
                onClick={() => document.getElementById('csv-upload').click()}
              >
                <CloudUpload sx={{ fontSize: 48, color: 'primary.main', mb: 1 }} />
                <Typography variant="h6" gutterBottom>
                  Click to upload CSV file
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  or drag and drop your file here
                </Typography>
                <input
                  id="csv-upload"
                  type="file"
                  accept=".csv"
                  style={{ display: 'none' }}
                  onChange={handleFileUpload}
                />
              </Box>

              {importDialog.file && (
                <Alert severity="success" sx={{ mt: 2 }}>
                  File selected: {importDialog.file.name}
                </Alert>
              )}
            </Box>
          )}

          {importDialog.step === 1 && (
            <Box>
              <Typography variant="h6" gutterBottom>
                Preview Data ({importDialog.previewData.length} records)
              </Typography>
              
              <TableContainer component={Paper} sx={{ maxHeight: 400 }}>
                <Table stickyHeader size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Row</TableCell>
                      <TableCell>Name</TableCell>
                      <TableCell>Email</TableCell>
                      <TableCell>Status</TableCell>
                      <TableCell>Validation</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {importDialog.previewData.map((row) => (
                      <TableRow key={row._rowId}>
                        <TableCell>{row._rowId}</TableCell>
                        <TableCell>{row.fullName}</TableCell>
                        <TableCell>{row.email}</TableCell>
                        <TableCell>{row.status || 'pending'}</TableCell>
                        <TableCell>
                          {row.errors.length === 0 ? (
                            <CheckIcon color="success" fontSize="small" />
                          ) : (
                            <Tooltip title={row.errors.join(', ')}>
                              <ErrorIcon color="error" fontSize="small" />
                            </Tooltip>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>

              <Alert severity="info" sx={{ mt: 2 }}>
                Please review the data before importing. Rows with errors will be skipped.
              </Alert>
            </Box>
          )}

          {importDialog.step === 2 && (
            <Box>
              <Typography variant="h6" gutterBottom>
                Import Results
              </Typography>
              
              <Alert 
                severity={importDialog.importResults.errors.length === 0 ? "success" : "warning"}
                sx={{ mb: 2 }}
              >
                Successfully imported {importDialog.importResults.success} bookkeepers
              </Alert>

              {importDialog.importResults.errors.length > 0 && (
                <Box>
                  <Typography variant="subtitle1" gutterBottom>
                    Errors ({importDialog.importResults.errors.length}):
                  </Typography>
                  <List dense sx={{ maxHeight: 200, overflow: 'auto' }}>
                    {importDialog.importResults.errors.map((error, index) => (
                      <ListItem key={index}>
                        <ListItemIcon>
                          <ErrorIcon color="error" />
                        </ListItemIcon>
                        <ListItemText primary={error} />
                      </ListItem>
                    ))}
                  </List>
                </Box>
              )}
            </Box>
          )}
        </DialogContent>

        <DialogActions>
          {importDialog.step === 0 && (
            <Button onClick={handleImportClose}>Cancel</Button>
          )}
          
          {importDialog.step === 1 && (
            <>
              <Button onClick={() => setImportDialog(prev => ({ ...prev, step: 0 }))}>
                Back
              </Button>
              <Button 
                onClick={handleImport}
                variant="contained"
                disabled={importDialog.importing}
                startIcon={importDialog.importing ? <CircularProgress size={20} /> : <Publish />}
              >
                {importDialog.importing ? 'Importing...' : 'Import Data'}
              </Button>
            </>
          )}
          
          {importDialog.step === 2 && (
            <Button onClick={handleImportClose} variant="contained">
              Done
            </Button>
          )}
        </DialogActions>
      </Dialog>

      {/* Extend Expiration Dialog */}
      <Dialog 
        open={extendDialog.open} 
        onClose={() => setExtendDialog({ open: false, bookkeeper: null, expirationMonths: 6 })}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Extend Expiration</DialogTitle>
        <DialogContent>
          <Typography variant="body1" paragraph>
            Extend expiration for <strong>{extendDialog.bookkeeper?.fullName}</strong>:
          </Typography>
          <FormControl fullWidth variant="outlined" sx={{ mt: 2 }}>
            <InputLabel>Extension Period</InputLabel>
            <Select
              value={extendDialog.expirationMonths}
              onChange={(e) => setExtendDialog(prev => ({
                ...prev,
                expirationMonths: e.target.value
              }))}
              label="Extension Period"
            >
              <MenuItem value={1}>1 Month</MenuItem>
              <MenuItem value={3}>3 Months</MenuItem>
              <MenuItem value={6}>6 Months</MenuItem>
              <MenuItem value={12}>12 Months</MenuItem>
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setExtendDialog({ open: false, bookkeeper: null, expirationMonths: 6 })}>
            Cancel
          </Button>
          <Button 
            onClick={handleExtendExpiration} 
            variant="contained" 
            color="primary"
          >
            Extend
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog 
        open={deleteDialog.open} 
        onClose={() => setDeleteDialog({ open: false, bookkeeper: null })}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Delete Bookkeeper</DialogTitle>
        <DialogContent>
          <Typography variant="body1">
            Are you sure you want to delete <strong>{deleteDialog.bookkeeper?.fullName}</strong>?
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            This action cannot be undone. The bookkeeper will be permanently removed from the system.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialog({ open: false, bookkeeper: null })}>
            Cancel
          </Button>
          <Button 
            onClick={handleDelete} 
            variant="contained" 
            color="error"
            startIcon={<Delete />}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* Bulk Actions Dialog */}
      <Dialog 
        open={bulkActionsDialog.open} 
        onClose={() => setBulkActionsDialog({ open: false, action: '' })}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          {bulkActionsDialog.action === 'approve' ? 'Approve Bookkeepers' : 'Delete Bookkeepers'}
        </DialogTitle>
        <DialogContent>
          <Typography variant="body1">
            Are you sure you want to {bulkActionsDialog.action} {selectedBookkeepers.length} bookkeeper(s)?
          </Typography>
          {bulkActionsDialog.action === 'delete' && (
            <Typography variant="body2" color="error" sx={{ mt: 1 }}>
              This action cannot be undone. The bookkeepers will be permanently removed from the system.
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setBulkActionsDialog({ open: false, action: '' })}>
            Cancel
          </Button>
          <Button 
            onClick={bulkActionsDialog.action === 'approve' ? handleBulkApprove : handleBulkDelete} 
            variant="contained" 
            color={bulkActionsDialog.action === 'approve' ? 'success' : 'error'}
          >
            {bulkActionsDialog.action === 'approve' ? 'Approve' : 'Delete'} {selectedBookkeepers.length} Bookkeepers
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Bookkeepers;